import { Text } from "@carbon6/c6-frontend-design";
import { useMemo, type ReactNode } from "react";
import {
  type TrainingEvalMetricsView,
  type TrainingEvalRunSummaryView,
  type TrainingEvalSummaryView,
} from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import type { LoadState } from "@/components/dataset";
import { successRateEmptyLabel } from "@/modules/evaluation/public";
import { sessionSimpleStatus } from "@/modules/training/public";
import { SessionStatusChip } from "@/components/status/SessionStatusChip";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { formatDurationMs } from "../trainingSessionDetailFormat";
import {
  evalOutcomeCounts,
  evalRunDurations,
  failureCount,
  successRateWithCount,
  type EvalOutcomeCounts,
  type EvalRunDurations,
} from "./sessionAnalysisModel";
import { SessionEvalVideoGallery } from "./SessionEvalVideoGallery";
import styles from "./ScenarioEvalSection.module.css";

type ScenarioEvalSectionProps = {
  evalState: LoadState<TrainingEvalSummaryView>;
  // 체크포인트 표기(epoch·budget_unit)를 위한 run 요약. eval_run_id 로 join 한다.
  summaries: readonly TrainingEvalRunSummaryView[];
  // Evaluation 정보 조회가 실패했을 때 다시 받는다.
  onRetry: () => void;
};

// 시나리오별 평가. eval run 1개 = 시나리오 1개 단위를 그대로 카드로 표시한다. 카드는 항상 펼쳐져
// 있어서 영상이 바로 재생 준비 상태다. 목록 조회는 비동기라 패널의 첫 페인트를 막지 않는다.
// 실패 영상이 없으면 갤러리가 경고로 표시한다.
export function ScenarioEvalSection({
  evalState,
  summaries,
  onRetry,
}: ScenarioEvalSectionProps): ReactNode {
  const summaryById = useMemo(
    () => new Map(summaries.map((summary) => [summary.id, summary])),
    [summaries],
  );

  return (
    <section className={styles.card} aria-label="시나리오별 Evaluation">
      <div className={styles.cardHeader}>
        <Text variant={hatcheryTextModes["caption-bold"]}>시나리오별 Evaluation</Text>
        {evalState.status === "success" && evalState.data.total > 0 ? (
          <span className={styles.headChips}>
            <span className={styles.cardMeta}>전체 {evalState.data.total}회</span>
            {Object.entries(evalState.data.by_status).map(([status, count]) => (
              <SessionStatusChip key={status} status={status}>
                {`${sessionSimpleStatus(status).label} ${count}`}
              </SessionStatusChip>
            ))}
          </span>
        ) : null}
      </div>
      {evalState.status === "idle" || evalState.status === "loading" ? (
        <LoadingState size="sm">Evaluation 정보를 불러오고 있습니다.</LoadingState>
      ) : evalState.status === "error" ? (
        <EmptyState size="sm" tone="error" action={{ label: "다시 시도", onClick: onRetry }}>
          Evaluation 정보를 불러오지 못했습니다: {evalState.message}
        </EmptyState>
      ) : evalState.data.total === 0 ? (
        <EmptyState size="sm">이 Session에는 아직 Evaluation 실행이 없습니다.</EmptyState>
      ) : (
        <>
          <div className={styles.cards}>
            {evalState.data.eval_runs.map((run) => (
              <ScenarioEvalCard
                key={run.eval_run_id}
                run={run}
                summary={summaryById.get(run.eval_run_id) ?? null}
              />
            ))}
          </div>
          <p className={styles.footnote}>
            성공 에피소드는 기본 설정에서 녹화되지 않을 수 있습니다.
          </p>
        </>
      )}
    </section>
  );
}

// 성공률과 시행 수를 함께 표시하는 라벨. n 없는 % 는 과신을 만들므로 시행 수가 있으면 반드시 붙인다.
function successRateLabel(
  run: TrainingEvalMetricsView,
  summary: TrainingEvalRunSummaryView | null,
): string {
  const { rate, trials, successes } = successRateWithCount(run.metrics_all);
  if (rate !== null && trials !== null && successes !== null) {
    return `${Math.round(rate * 100)}% · ${successes}/${trials}회`;
  }
  if (rate !== null) {
    return `${Math.round(rate * 100)}%`;
  }
  if (summary?.success_rate != null) {
    return `${Math.round(summary.success_rate * 100)}%`;
  }
  // 빈 이유를 합치지 않는다. 대기/수집 중은 기다리면 값이 오고, "—" 는 오지 않는다.
  return successRateEmptyLabel(summary?.success_rate_state ?? null);
}

// 평가에 쓴 체크포인트 라벨. run 요약의 epoch(정수)와 budget_unit 으로 만든다.
function evaluatedCheckpointLabel(summary: TrainingEvalRunSummaryView | null): string {
  if (summary?.epoch == null) {
    return "—";
  }
  const unit = summary.budget_unit;
  return `${unit === "step" ? "STEP" : "EPOCH"} ${summary.epoch}`;
}

// 시간 라벨. 실행이 차지한 전체 길이(소요)와 평가기가 측정한 순수 평가 길이를 나눠 함께 표시한다.
// 둘의 차이가 파드 준비·체크포인트 다운로드·아티팩트 업로드 비용이다.
function durationLabel(durations: EvalRunDurations): string {
  const elapsed = `소요 ${formatDurationMs(durations.elapsedMs)}`;
  if (durations.computeMs === null) {
    return elapsed;
  }
  return `${elapsed} · Evaluation ${formatDurationMs(durations.computeMs)}`;
}

// 판정 내역. 성공률만으로는 0% 의 원인(실패인지 타임아웃인지)이 보이지 않는다.
function outcomeLabel(counts: EvalOutcomeCounts): string {
  return `성공 ${counts.success} · 실패 ${counts.failure} · 타임아웃 ${counts.timeout} · 오탐 ${counts.falsePositive}`;
}

function ScenarioEvalCard({
  run,
  summary,
}: {
  run: TrainingEvalMetricsView;
  summary: TrainingEvalRunSummaryView | null;
}): ReactNode {
  const outcomes = evalOutcomeCounts(run.metrics_all);

  return (
    <div className={styles.evalCard}>
      <div className={styles.evalSummary}>
        <span className={styles.scenarioName}>{run.scenario ?? "—"}</span>
        {/* 실행(job) 축과 평가 결과 축을 라벨로 구분한다. 붙여 두면 job 성공 칩이 성공률 0% 를
            부정하는 것처럼 읽힌다. */}
        <span className={styles.axisRow}>
          <span className={styles.axisLabel}>실행</span>
          <SessionStatusChip status={run.status} />
          <span className={styles.rowFact}>Checkpoint {evaluatedCheckpointLabel(summary)}</span>
          <span className={styles.rowFact}>{durationLabel(evalRunDurations(run, summary))}</span>
        </span>
        <span className={styles.axisRow}>
          <span className={styles.axisLabel}>결과</span>
          <span className={styles.rowFact}>
            성공률 <strong>{successRateLabel(run, summary)}</strong>
          </span>
          {outcomes === null ? null : (
            <span className={styles.rowFact}>{outcomeLabel(outcomes)}</span>
          )}
        </span>
      </div>
      {/* 갤러리를 항상 마운트한다. 영상 signed URL 은 TTL(30분) 안에 재생하는 것을 전제로 한다. */}
      <div className={styles.evalBody}>
        <SessionEvalVideoGallery
          evalRunId={run.eval_run_id}
          expectedFailures={failureCount(run.metrics_all)}
        />
      </div>
    </div>
  );
}
